import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Box, Collapsible, Field, Fieldset, Flex, InputGroup, NativeSelect, Stack, } from '@chakra-ui/react';
import { CheckoutHeading } from '@client/components/checkout/CheckoutHeading';
import { CheckoutField } from '@client/components/checkout/CheckoutField';
import { CheckoutFieldError } from '@client/components/checkout/CheckoutFieldError';
import { CHECKOUT_FIELD_GAP, CHECKOUT_FIELD_HEIGHT, } from '@client/components/checkout/checkoutLayout';
import { Layout } from '@client/constants';
import { useShoppingCart } from '@client/providers/ShoppingCartProvider';
import { fieldErrorColor, sansFontFamily } from '@client/theme';
import { extractAddressFields } from '@client/utils/addressUtils';
import { US_STATES } from '@heirloom/common/constants';
import { useLoadScript } from '@react-google-maps/api';
import { useEffect, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
const LIBRARIES = ['places'];
export const CheckoutShippingForm = (props) => {
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY,
        libraries: LIBRARIES,
    });
    const { shippingAddress, shippingAddressErrors, shippingAddressUndeliverable, setShippingAddress, clearShippingAddressError, } = useShoppingCart();
    const debounceRef = useRef(undefined);
    const containerRef = useRef(null);
    const [addressInput, setAddressInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current &&
                !containerRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const fetchSuggestions = (value) => {
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        if (!value.trim() || !isLoaded) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            const { suggestions: results } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: value,
                includedRegionCodes: ['us'],
                includedPrimaryTypes: ['street_address'],
            });
            setSuggestions(results);
            setShowSuggestions(true);
        }, 300);
    };
    const handleSelect = async (suggestion) => {
        setShowSuggestions(false);
        setSuggestions([]);
        const prediction = suggestion.placePrediction;
        if (!prediction)
            return;
        const place = prediction.toPlace();
        await place.fetchFields({
            fields: ['addressComponents'],
        });
        const fields = extractAddressFields(place.addressComponents || []);
        setShippingAddress({ ...shippingAddress, ...fields });
        setAddressInput(fields.line1 || prediction.text?.text || '');
    };
    return (_jsxs(Stack, { gap: 0, children: [_jsx(CheckoutHeading, { children: "Ship To" }), _jsxs(Fieldset.Root, { size: "lg", mt: CHECKOUT_FIELD_GAP, disabled: props.disabled, children: [_jsxs(Flex, { gap: CHECKOUT_FIELD_GAP, direction: props.layout === Layout.MOBILE
                            ? 'column'
                            : 'row', children: [_jsx(Field.Root, { children: _jsx(CheckoutField, { placeholder: "First name", name: "given-name", autoComplete: "given-name", value: shippingAddress.firstName, onChange: (e) => setShippingAddress({
                                        ...shippingAddress,
                                        firstName: e.target.value,
                                    }) }) }), _jsxs(Field.Root, { gap: 0, children: [_jsx(CheckoutField, { placeholder: "Last name", name: "family-name", autoComplete: "family-name", value: shippingAddress.lastName, onChange: (e) => {
                                            setShippingAddress({
                                                ...shippingAddress,
                                                lastName: e.target.value,
                                            });
                                            clearShippingAddressError('lastName');
                                        }, invalid: !!shippingAddressErrors.lastName }), _jsx(CheckoutFieldError, { errorText: shippingAddressErrors.lastName })] })] }), _jsx(Field.Root, { children: _jsxs(Box, { ref: containerRef, position: "relative", width: "100%", children: [_jsx(InputGroup, { startElement: _jsx(FaSearch, {}), children: _jsx(CheckoutField, { placeholder: "Address", name: "address-line1", value: addressInput ||
                                            shippingAddress.line1, onChange: (e) => {
                                            setShippingAddress({
                                                ...shippingAddress,
                                                line1: e.target.value,
                                            });
                                            clearShippingAddressError('line1');
                                            setAddressInput(e.target.value);
                                            if (document.activeElement ===
                                                e.target) {
                                                fetchSuggestions(e.currentTarget.value);
                                            }
                                        }, onFocus: () => suggestions.length > 0 &&
                                            setShowSuggestions(true), autoComplete: "address-line1", invalid: !!shippingAddressErrors.line1 }) }), showSuggestions &&
                                    suggestions.length > 0 && (_jsx(Box, { position: "absolute", top: "100%", left: 0, right: 0, zIndex: "dropdown", bg: "white", borderRadius: "md", boxShadow: "md", mt: 1, overflow: "hidden", children: suggestions.map((suggestion, i) => (_jsx(Box, { px: 3, py: 2, cursor: "pointer", fontSize: 16, _hover: {
                                            bg: 'gray.100',
                                        }, onMouseDown: () => handleSelect(suggestion), children: suggestion
                                            .placePrediction
                                            ?.text?.text }, i))) })), _jsx(CheckoutFieldError, { errorText: shippingAddressErrors.line1 })] }) }), _jsx(Field.Root, { children: _jsx(CheckoutField, { name: "address-line2", autoComplete: "address-line2", placeholder: "Apartment, suite, etc. (optional)", value: shippingAddress.line2, onChange: (e) => setShippingAddress({
                                ...shippingAddress,
                                line2: e.target.value,
                            }) }) }), _jsxs(Flex, { gap: CHECKOUT_FIELD_GAP, direction: props.layout === Layout.MOBILE
                            ? 'column'
                            : 'row', children: [_jsxs(Field.Root, { gap: 0, children: [_jsx(CheckoutField, { placeholder: "City", name: "city", autoComplete: "address-level2", value: shippingAddress.city, onChange: (e) => {
                                            setShippingAddress({
                                                ...shippingAddress,
                                                city: e.target.value,
                                            });
                                            clearShippingAddressError('city');
                                        }, invalid: !!shippingAddressErrors.city }), _jsx(CheckoutFieldError, { errorText: shippingAddressErrors.city })] }), _jsxs(Field.Root, { gap: 0, children: [_jsxs(NativeSelect.Root, { variant: "subtle", size: "lg", children: [_jsx(NativeSelect.Field, { placeholder: "State", name: "address-level1", autoComplete: "address-level1", height: CHECKOUT_FIELD_HEIGHT, fontFamily: sansFontFamily, fontSize: 16, value: shippingAddress.state, onChange: (e) => {
                                                    setShippingAddress({
                                                        ...shippingAddress,
                                                        state: e.target.value,
                                                    });
                                                    clearShippingAddressError('state');
                                                }, border: shippingAddressErrors.state
                                                    ? `1px solid ${fieldErrorColor}`
                                                    : undefined, children: US_STATES.map((s) => (_jsx("option", { value: s.value, children: s.label }, s.value))) }), _jsx(NativeSelect.Indicator, {})] }), _jsx(CheckoutFieldError, { errorText: shippingAddressErrors.state })] }), _jsxs(Field.Root, { gap: 0, children: [_jsx(CheckoutField, { placeholder: "Zip code", name: "postal-code", autoComplete: "postal-code", value: shippingAddress.zip, onChange: (e) => {
                                            setShippingAddress({
                                                ...shippingAddress,
                                                zip: e.target.value,
                                            });
                                            clearShippingAddressError('zip');
                                        }, invalid: !!shippingAddressErrors.zip }), _jsx(CheckoutFieldError, { errorText: shippingAddressErrors.zip })] })] })] }), _jsx(Collapsible.Root, { open: Boolean(shippingAddressUndeliverable), children: _jsx(Collapsible.Content, { pt: CHECKOUT_FIELD_GAP, children: _jsxs(Alert.Root, { status: "error", children: [_jsx(Alert.Indicator, {}), _jsx(Alert.Content, { children: _jsx(Alert.Title, { children: "Address validation failed." }) })] }) }) })] }));
};
